'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import EmbeddedTour from '../../components/embedded_matterport';
import ImageCarouselSwiper from '../../components/image_carousel_swiper';
import VideoModal from '../../components/video_modal';
import { logFirebaseEvent } from '../../lib/firebase/app';

interface TourMobileProps {
    matterportId?: string;
    youVisitUrl?: string;
    images: string[];
    altTexts?: Record<string, string>;
    videoUrl?: string;
}

const TourMobile: React.FC<TourMobileProps> = ({ matterportId, youVisitUrl, images, altTexts = {}, videoUrl }) => {
    const [mobileShowing3DTour, setMobileShowing3DTour] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const tourContainerRef = useRef<HTMLDivElement>(null);

    const has3DTour = Boolean(matterportId || youVisitUrl);
    const tourType = matterportId ? 'matterport' : 'youvisit';
    const tourId = matterportId || youVisitUrl;

    // Add additional scroll prevention
    useEffect(() => {
        const preventScroll = (e: TouchEvent) => {
            if (mobileShowing3DTour) {
                e.preventDefault();
            }
        };

        // Only add listeners when the 3D tour is showing
        if (mobileShowing3DTour && tourContainerRef.current) {
            const container = tourContainerRef.current;
            container.addEventListener('touchmove', preventScroll, { passive: false });

            return () => {
                container.removeEventListener('touchmove', preventScroll);
            };
        }
    }, [mobileShowing3DTour]);

    const handleTourToggle = () => {
        const newState = !mobileShowing3DTour;
        setMobileShowing3DTour(newState);

        if (newState && has3DTour) {
            logFirebaseEvent('3d_tour_view', {
                tourId,
                tourType,
                timestamp: new Date().toISOString(),
                source: 'mobile_gallery'
            });
        }
    };

    const handleVideoClick = (index: number) => {
        if (videoUrl && index === 0) { // Check if this is the video slide (now first item)
            setIsVideoModalOpen(true);
        }
    };

    // Prepare images array with video thumbnail as the first item (if video exists)
    const prepareCarouselItems = () => {
        if (!videoUrl) return images.map(url => ({ src: url, alt: altTexts[url] || 'Venue image' }));

        // Get the first image as a thumbnail for the video if available
        const videoThumbnail = images.length > 0 ? images[0] : '';

        const items = [
            {
                src: videoThumbnail,
                alt: 'Venue image',
                isVideo: true,
                videoUrl
            },
            ...images.map(url => ({ src: url, alt: altTexts[url] || 'Venue image' }))
        ];

        return items;
    };

    // Ensure the tour container has enough height for both tour types
    const containerHeight = tourType === 'youvisit' ? '450px' : '400px';

    return (
        <Box sx={{
            width: '100%',
            aspectRatio: '4/3',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
        }}>
            {/* Video Modal */}
            {videoUrl && (
                <VideoModal
                    videoUrl={videoUrl}
                    open={isVideoModalOpen}
                    onClose={() => setIsVideoModalOpen(false)}
                    autoPlay={true}
                />
            )}

            <Box
                ref={tourContainerRef}
                sx={{
                    flex: 1,
                    width: '100%',
                    position: 'relative',
                    aspectRatio: has3DTour ? '4/3' : '4/3',
                    height: mobileShowing3DTour ? containerHeight : 'auto',
                    touchAction: mobileShowing3DTour ? 'none' : 'auto', // Disable touch actions when showing 3D tour
                }}
            >
                {mobileShowing3DTour && has3DTour ? (
                    <div className="touch-none">
                        <EmbeddedTour
                            modelId={tourId}
                            tourType={tourType}
                            height={containerHeight}
                        />
                    </div>
                ) : (
                    <ImageCarouselSwiper
                        images={prepareCarouselItems()}
                        onSlideClick={handleVideoClick}
                    />
                )}
            </Box>
            {has3DTour && (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <button
                        onClick={handleTourToggle}
                        className="w-[95%] py-2 px-0 bg-[#FF385C] text-white flex items-center justify-center gap-1 font-['Inter'] animate-pulse mt-1 rounded-lg"
                        style={{ margin: '1rem 0' }}
                    >
                        {mobileShowing3DTour ? 'Show Images' : 'Show 3D Tour'}
                    </button>
                </Box>
            )}
        </Box>
    );
};

export default TourMobile;
export { TourMobile as MatterportMobile }; // For backward compatibility
